import { type FormEvent, useState } from 'react';
import { errorText, joinInvite } from '../App';
import { maySignIn } from '../lib/account';
import { adRef, forgetAdRef } from '../lib/ad';
import { api, type Session, saveSession } from '../lib/api';
import { campaignTags, forgetCampaign } from '../lib/campaign';
import { saveGuestKey } from '../lib/guest';
import { useNicknameCheck } from '../lib/nickname';
import { heldOpening } from '../lib/push';
import { useUi } from '../store/ui';
import { S } from '../strings';
import { AccountDoor } from '../ui/AccountDoor';
import { Logo } from '../ui/Logo';

/**
 * Playing outside Lagos Life: a name is the whole identity. Standalone (PF-19) it makes a guest whose
 * key this phone keeps; on a staging build without standalone, the dev login. Lagos Life players
 * never see this.
 */
export function Login() {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const set = useUi((s) => s.set);
  const standalone = useUi((s) => s.config?.platforms?.standalone === true);
  useUi((s) => s.config);
  /** I don play before: sign in with a saved profile (PF-19). */
  const [back, setBack] = useState(false);
  // a nickname is one player's: a visitor's name is checked against them as it is typed
  const taken = useNicknameCheck(standalone ? name : '', 2)?.reason === 'nickname_taken';

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      let s: Session;
      if (standalone) {
        const g = await api.guest(name, adRef(), campaignTags());
        saveGuestKey(g.guestKey);
        forgetAdRef();
        forgetCampaign();
        s = g;
      } else s = await api.devLogin(name);
      saveSession(s);
      // a wahala.io/play/{screen} link a new player came in with goes there, not to the menu
      set({ me: s.user, account: standalone ? 'guest' : null, screen: heldOpening()?.screen ?? 'menu', notice: null });
      await joinInvite();
    } catch (err) {
      set({ notice: errorText(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="screen center login-screen">
      <Logo />
      <form className="card login" onSubmit={submit}>
        <h2 className="display small">{S.login.title}</h2>
        {/* the name and Enter on one line; the title asks the question, the label is for readers */}
        <div className="join-row">
          <label className="sr-only" htmlFor="driver-name">
            {S.login.label}
          </label>
          <input
            id="driver-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={S.login.placeholder}
            maxLength={16}
            autoComplete="nickname"
            enterKeyHint="go"
            aria-invalid={taken}
            aria-describedby="driver-name-note"
          />
          <button
            className="wh-btn wh-btn--carry wh-btn--md"
            type="submit"
            disabled={busy || taken || name.trim().length < 2}
          >
            {S.login.go}
          </button>
        </div>
        <p id="driver-name-note" className={taken ? 'login__taken small' : 'muted small'} aria-live="polite">
          {taken ? S.login.taken : standalone ? S.login.guestNote : S.login.note}
        </p>
        {standalone && maySignIn() && (
          <button
            type="button"
            className="wh-btn wh-btn--md wh-btn--block login__back"
            aria-haspopup="dialog"
            onClick={() => setBack(true)}
          >
            {S.account.signin.open}
          </button>
        )}
      </form>
      {back && <AccountDoor start="signin" onClose={() => setBack(false)} />}
    </main>
  );
}
